import {inject, Service, signal} from '@angular/core';
import {AppQuery} from '../../queries/app.query';
import {ModalService} from '../modal/modal.service';
import {AppSnackbarTypeEnum} from '../../enums/modal/app-snackbar-type.enum';
import {AppSnackbarCloseReasonEnum} from '../../enums/modal/app-snackbar-close-reason.enum';
import {TranslationKey} from '../../types/available-translations.type';
import {AppSnackbarModal} from '../../models/modal/snackbar/app-snackbar-modal.model';
import {SnackbarModalComponent} from '../../components/modals/snackbar-modal/snackbar-modal.component';
import {AppModalVerticalPositionEnum} from '../../enums/modal/app-modal-vertical-position.enum';
import {AppModalHorizontalPositionEnum} from '../../enums/modal/app-modal-horizontal-position.enum';
import {AppModalTypeEnum} from '../../enums/modal/app-modal-type.enum';

const SNACK_BAR_DEFAULT_ACTION_TRANSLATION_KEY: TranslationKey = 'BUTTON.OK'; //TODO: CONFIG

@Service()
export class SnackbarService {
    private readonly appQuery: AppQuery = inject(AppQuery);
    private readonly modalService: ModalService = inject(ModalService);

    /**
     * Shows a neutral info snackbar that disappears on its own.
     *
     * @param translationKey the message; also used to build the `data-qa` tag.
     * @param translateKeyParameters values for the placeholders inside that key.
     * @param actionMessage label of the dismiss button, "OK" by default.
     */
    public showInfo(
        translationKey: TranslationKey,
        translateKeyParameters?: Record<string, unknown>,
        actionMessage: TranslationKey = SNACK_BAR_DEFAULT_ACTION_TRANSLATION_KEY): void {
        this.showSnackbar(AppSnackbarTypeEnum.Info, 'info-snackbar-' + translationKey, translationKey, translateKeyParameters, undefined, actionMessage);
    }

    /**
     * Confirms a completed action with a success snackbar that disappears on its own.
     *
     * @param translationKey the message; also used to build the `data-qa` tag.
     * @param translateKeyParameters values for the placeholders inside that key.
     * @param actionMessage label of the dismiss button, "OK" by default.
     * @param modalOverrides fields of the snackbar modal to override, e.g. `{shouldAutoFocus: false}` to leave the focus where it is.
     * @returns the modal, so the caller can react to it closing via `afterClosed()` or close it early.
     */
    public showSuccess(
        translationKey: TranslationKey,
        translateKeyParameters?: Record<string, unknown>,
        actionMessage: TranslationKey = SNACK_BAR_DEFAULT_ACTION_TRANSLATION_KEY,
        modalOverrides?: Partial<AppSnackbarModal>): AppSnackbarModal {
        return this.showSnackbar(AppSnackbarTypeEnum.Success, 'success-snackbar-' + translationKey, translationKey, translateKeyParameters, undefined, actionMessage, modalOverrides);
    }

    /**
     * Warns about something that did not fail outright. Disappears on its own.
     *
     * @param translationKey the message; also used to build the `data-qa` tag.
     * @param translateKeyParameters values for the placeholders inside that key.
     * @param actionMessage label of the dismiss button, "OK" by default.
     */
    public showWarning(
        translationKey: TranslationKey,
        translateKeyParameters?: Record<string, unknown>,
        actionMessage: TranslationKey = SNACK_BAR_DEFAULT_ACTION_TRANSLATION_KEY): void {
        this.showSnackbar(AppSnackbarTypeEnum.Warning, 'warning-snackbar-' + translationKey, translationKey, translateKeyParameters, undefined, actionMessage);
    }

    /**
     * Reports a failure to the user. This is the whole error handling for a caught error: pass the caught object as `errorObject` and it is
     * logged to the console as well, so no extra `console.error` is needed.
     *
     * @param translationKey the user-facing message — never the raw error text.
     * @param errorObject the caught error; only logged, never shown to the user.
     * @param translateKeyParameters values for the placeholders inside the message key.
     * @param actionMessage label of the dismiss button, "OK" by default.
     * @returns the modal, so the caller can react to it closing via `afterClosed()` or close it early.
     */
    public showError(
        translationKey: TranslationKey,
        errorObject?: Error,
        translateKeyParameters?: Record<string, unknown>,
        actionMessage: TranslationKey = SNACK_BAR_DEFAULT_ACTION_TRANSLATION_KEY): AppSnackbarModal {
        if (errorObject) {
            console.error(translationKey, errorObject);
        }

        return this.showSnackbar(AppSnackbarTypeEnum.Error, 'error-snackbar-' + translationKey, translationKey, translateKeyParameters, undefined, actionMessage);
    }

    /**
     * Shows the unspecific "something went wrong" error and logs the given error. Use it when there is no meaningful message for the user.
     *
     * @param errorObject the caught error; only logged, never shown to the user.
     */
    public showGenericError(errorObject?: Error): void {
        this.showError('ERROR.GENERIC', errorObject);
    }

    /**
     * Shows a snackbar without a timeout, e.g. for a running background task. It stays until the user dismisses it, another snackbar
     * replaces it, or the caller closes it via `modalService.closeModal(snackbar)` — so keep the returned modal.
     *
     * @param snackbarType decides colour and icon.
     * @param qaTag the `data-qa` value for tests; must be unique for this snackbar.
     * @param translationKey the message.
     * @param translateKeyParameters values for the placeholders inside that key.
     * @param actionMessage label of the dismiss button; without it the snackbar has no button and can only be closed from the code.
     * @returns the modal, needed to close it later.
     */
    public showPermanentSnackbar(snackbarType: AppSnackbarTypeEnum,
                                 qaTag: string,
                                 translationKey: TranslationKey,
                                 translateKeyParameters?: Record<string, unknown>,
                                 actionMessage?: TranslationKey): AppSnackbarModal {
        return this.showSnackbar(snackbarType, qaTag, translationKey, translateKeyParameters, 0, actionMessage);
    }

    /**
     * The underlying method of all snackbars — prefer {@link showInfo}, {@link showSuccess}, {@link showWarning} or {@link showError} and
     * only use this one when you need a custom qa tag or timeout.
     *
     * Only one snackbar is visible at a time: showing a new one closes the previous one.
     *
     * @param snackbarType decides colour and icon.
     * @param qaTag the `data-qa` value for tests; must be unique for this snackbar.
     * @param translationKey the message.
     * @param translateKeyParameters values for the placeholders inside that key.
     * @param timeoutInMS how long it stays visible; defaults to the configured toast delay. `0` and `undefined` mean it never closes by itself.
     * @param actionMessage label of the dismiss button; without it the snackbar has no button.
     * @param modalOverrides fields of the snackbar modal to override, e.g. `{shouldAutoFocus: false}` to leave the focus where it is.
     * @returns the modal, so it can be closed early via `modalService.closeModal(snackbar)`.
     */
    public showSnackbar(snackbarType: AppSnackbarTypeEnum,
                        qaTag: string,
                        translationKey: TranslationKey,
                        translateKeyParameters?: Record<string, unknown>,
                        timeoutInMS: number | undefined = this.appQuery.getToastDelayMS(),
                        actionMessage?: TranslationKey,
                        modalOverrides?: Partial<AppSnackbarModal>): AppSnackbarModal {
        const snackbar: AppSnackbarModal = this.createSnackbar({
            modalData: {
                snackbarType,
                translationKey,
                translateKeyParameters,
                actionTranslationKey: actionMessage,
                timeoutInMS
            },
            qaTag,
            isShowing: signal(true),
            ...modalOverrides
        });
        this.modalService.replaceAllByModalType(snackbar);
        if (timeoutInMS !== undefined && timeoutInMS !== 0) {
            setTimeout(() => {
                this.modalService.closeModal(snackbar, AppSnackbarCloseReasonEnum.Timeout);
            }, timeoutInMS);
        }
        return snackbar;
    }

    /**
     * Builds a bottom-centered snackbar modal. It is not shown yet — the caller has to hand it to the modal service.
     *
     * No type parameters here: unlike a dialog a snackbar always renders the same `SnackbarModalComponent`, so its type is fixed to
     * `AppSnackbarModal` — data is always `AppSnackbarData` and the close result is always an `AppSnackbarCloseReasonEnum`.
     *
     * ```ts
     * const snackbar = this.createSnackbar({
     *     modalData: { snackbarType, translationKey, translateKeyParameters, actionTranslationKey, timeoutInMS },
     *     qaTag: 'my-snackbar',
     *     isShowing: signal(true)
     * });
     * this.modalService.replaceAllByModalType(snackbar);
     * snackbar.afterClosed(closeReason => ...); // AppSnackbarCloseReasonEnum | undefined
     * ```
     *
     * @param snackbarModal the fields to set; `modalData` and `isShowing: signal(true)` are the ones that matter, the rest (component,
     *                        position, type) already has snackbar defaults.
     */
    private createSnackbar(
        snackbarModal: Partial<AppSnackbarModal> = {}
    ): AppSnackbarModal {
        return this.modalService.createModal<AppSnackbarModal>({
            component: SnackbarModalComponent,
            verticalPosition: AppModalVerticalPositionEnum.Bottom,
            horizontalPosition: AppModalHorizontalPositionEnum.Center,
            modalType: AppModalTypeEnum.Snackbar,
            shouldAutoFocus: true,
            qaTag: 'generic-snackbar',
            ...snackbarModal
        });
    }
}
